import React, { useMemo, useState } from 'react';
import {
  validateActivityPayload,
  GAME_MODE_CATALOG,
  WallDebatePreview,
  type WallPayload,
} from '@interactive-room';
import { StanceColorRail } from '../components/StanceColorRail';
import { serenityHexById, serenityIdFromHex } from '../constants/serenityPalette';
import type { BuilderStanceRow } from '../types/builder.types';
import { emptyWallPayload } from '../emptyPayloads';
import { StudioLiveShell } from '../components/StudioLiveShell';
import { createDraftId, saveActivityDraft, type ActivityDraft } from '../activityStorage';
import { resolveStudioLaunchDefaults } from '../studioLaunch';
import { generateSubjectAwareActivityDraft } from '../studioActivityGeneration';
import type { StudioLiveEditorProps } from '../types/editor.types';

function stancesFromPayload(stances: WallPayload['stances']): BuilderStanceRow[] {
  return stances.map((s, i) => ({
    id: s.id || `stance_${i + 1}`,
    label: s.label,
    colorId: serenityIdFromHex(s.color),
  }));
}

function stancesToPayload(rows: BuilderStanceRow[]): WallPayload['stances'] {
  return rows.map((row, i) => ({
    id: row.id || `stance_${i + 1}`,
    label: row.label.trim() || `Postura ${i + 1}`,
    color: serenityHexById(row.colorId),
  }));
}

export function WallLiveEditor({
  draftId,
  embedded,
  onSaved,
  launchSubjectId,
  launchPresetId,
}: StudioLiveEditorProps) {
  const launch = resolveStudioLaunchDefaults(emptyWallPayload, {
    draftId,
    launchSubjectId,
    launchPresetId,
  });
  const initialPayload = launch.payload;
  const [title, setTitle] = useState(initialPayload.title);
  const [prompt, setPrompt] = useState(initialPayload.prompt);
  const [stanceRows, setStanceRows] = useState<BuilderStanceRow[]>(() =>
    stancesFromPayload(initialPayload.stances),
  );
  const [subjectTag, setSubjectTag] = useState(() => launch.subjectTag);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [savedId] = useState(draftId ?? createDraftId());

  const meta = GAME_MODE_CATALOG.wall;
  const stances = useMemo(() => stancesToPayload(stanceRows), [stanceRows]);

  const payload = useMemo<WallPayload>(
    () => ({
      title,
      prompt,
      maxChars: initialPayload.maxChars ?? 140,
      stances,
    }),
    [title, prompt, stances, initialPayload.maxChars],
  );

  const updateStanceLabel = (stanceId: string, label: string) => {
    setStanceRows((rows) => rows.map((r) => (r.id === stanceId ? { ...r, label } : r)));
  };

  const save = () => {
    try {
      validateActivityPayload('wall', payload);
      setError(null);
      onSaved?.(
        saveActivityDraft({
          id: savedId,
          mode: 'wall',
          title: payload.title,
          subjectTag: subjectTag.trim() || 'general',
          payload,
        }),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error');
    }
  };

  const handleAiBuild = async (prompt: string) => {
    setAiLoading(true);
    setError(null);
    setNotice(null);
    try {
      const { draft, notice: aiNotice } = await generateSubjectAwareActivityDraft({
        mode: 'wall',
        subjectId: launchSubjectId,
        subjectTag: subjectTag.trim() || undefined,
        tuning: prompt,
        draftId: savedId,
        persist: false,
      });
      const next = draft.payload as WallPayload;
      setTitle(next.title);
      setPrompt(next.prompt);
      setStanceRows(stancesFromPayload(next.stances));
      if (draft.subjectTag) setSubjectTag(draft.subjectTag);
      if (aiNotice) setNotice(aiNotice);
    } catch (e) {
      setError(
        e instanceof Error && e.message === 'subject_context_required'
          ? 'Selecciona una asignatura desde Herramientas para generar con IA.'
          : e instanceof Error
            ? e.message
            : 'No se pudo generar con IA',
      );
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <StudioLiveShell
      embedded={embedded}
      error={error}
      subjectTag={subjectTag}
      onSubjectTagChange={setSubjectTag}
      onSave={save}
      aiActivityLabel="debate"
      onAiBuild={handleAiBuild}
      aiLoading={aiLoading}
      sidebar={<StanceColorRail stances={stanceRows} onChange={setStanceRows} />}
      sidebarTitle="Columnas"
      sidebarMinimal
    >
      {notice ? (
        <p className="mx-4 mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[10px] text-amber-900">
          {notice}
        </p>
      ) : null}
      <header className="plugin-live-toolbar plugin-live-toolbar--ghost">
        <input
          className="canvas-inline-input canvas-inline-input--title min-w-0 flex-1 bg-transparent font-display text-base font-semibold tracking-tight"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Título del debate"
        />
        <span className="studio-pill">{meta.displayName}</span>
      </header>

      <div className="studio-canvas studio-canvas--wall min-h-0 flex-1 overflow-hidden p-3 sm:p-4">
        <WallDebatePreview
          stances={stances}
          prompt={prompt}
          editable
          onPromptChange={setPrompt}
          onStanceLabelChange={updateStanceLabel}
        />
      </div>
    </StudioLiveShell>
  );
}
